import { motion } from "framer-motion";

export function SectionEyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

export function SectionHeading({ children, className = "" }) {
  return (
    <h2 className={`font-serif text-4xl md:text-5xl lg:text-6xl text-pe-ink leading-[1.05] tracking-tight ${className}`}>
      {children}
    </h2>
  );
}

export function FadeUp({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * SectionWrap — consistent vertical rhythm and container width.
 */
export function SectionWrap({ children, className = "", id, testId, tone = "cream" }) {
  const toneClasses = {
    cream: "bg-pe-cream text-pe-ink",
    mauve: "bg-pe-mauve/40 text-pe-ink",
    ink: "bg-pe-ink text-pe-cream",
    rose: "bg-pe-rose text-white",
    white: "bg-white text-pe-ink",
  }[tone];
  return (
    <section
      id={id}
      data-testid={testId}
      className={`relative py-20 md:py-28 lg:py-32 ${toneClasses} ${className}`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">{children}</div>
    </section>
  );
}

export function StatCard({ value, label, accent = "maroon", testId }) {
  const accentColor =
    accent === "orange" ? "text-pe-orange" : accent === "rose" ? "text-pe-rose" : "text-pe-maroon";
  return (
    <div data-testid={testId} className="border-t border-pe-ink/10 pt-5 md:pt-7">
      <div className={`font-serif text-4xl md:text-5xl lg:text-6xl ${accentColor} leading-none`}>{value}</div>
      <div className="mt-3 text-[11px] md:text-xs tracking-[0.18em] uppercase text-pe-ink/70">{label}</div>
    </div>
  );
}

/**
 * CtaBand — reusable full-bleed CTA section.
 */
export function CtaBand({ eyebrow, heading, body, ctaLabel, ctaHref, secondaryLabel, secondaryHref, testId = "cta-band" }) {
  return (
    <section data-testid={testId} className="relative bg-pe-ink text-pe-cream py-20 md:py-28">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 lg:px-14 text-center">
        {eyebrow && <div className="eyebrow text-pe-orange mb-4">{eyebrow}</div>}
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6 text-pe-cream">{heading}</h2>
        {body && <p className="text-pe-cream/80 max-w-2xl mx-auto text-base md:text-lg leading-relaxed mb-10">{body}</p>}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a
            data-testid={`${testId}-primary-cta`}
            href={ctaHref}
            className="bg-pe-orange text-pe-ink hover:bg-white transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
          >
            {ctaLabel}
          </a>
          {secondaryLabel && (
            <a
              data-testid={`${testId}-secondary-cta`}
              href={secondaryHref}
              className="border border-pe-cream/30 text-pe-cream hover:bg-white/10 transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
            >
              {secondaryLabel}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
